import { FC, ReactNode, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as BurgerSvg } from 'assets/svg/burger.svg';
import useOutsideClick from 'hooks/useOutsideClick';
import { UserProfilePictureResponse } from 'interfaces/user.interface';
import { joinMimeTypeAndDataToBase64 } from 'utils/files';
import { cn } from 'utils/global';

import Avatar from './Avatar';
import Banner from './Banner';
import Button from './button/Button';

interface Props {
  onClick: VoidFunction;
  children?: ReactNode;
  isSidebarVisible: boolean;
  isProfilePictureLoading: boolean;
  pictureData?: UserProfilePictureResponse;
  isLoading?: boolean;
  isSubscription?: boolean;
  isInTrialPeriod?: boolean;
  displayName?: string;
  tenantId?: number;
  subscriptionId?: number;
  subscriptionEndDateUtc?: string;
  subscriptionAwaitingCancellation?: boolean;
  isSubscriptionDeactivated?: boolean;
  expiringSubscriptionName?: string;
  pageTitle: string | undefined;
  firstName?: string;
  lastName?: string;
  email?: string;
}

const Header: FC<Props> = ({
  onClick,
  children,
  isSidebarVisible,
  pictureData,
  isProfilePictureLoading,
  isLoading,
  isSubscription,
  isInTrialPeriod,
  displayName,
  tenantId,
  subscriptionId,
  subscriptionEndDateUtc,
  subscriptionAwaitingCancellation,
  isSubscriptionDeactivated,
  expiringSubscriptionName,
  pageTitle,
  firstName,
  lastName,
  email,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const modalRef = useRef(null);
  const { t } = useTranslation();

  const { profilePicture, mimeType } = pictureData?.result || {};
  const profileAvatar = joinMimeTypeAndDataToBase64({ data: profilePicture, mimeType });

  const toggleModal = () => {
    if (!isModalVisible) {
      setIsModalVisible(true);
    }
  };

  const handleClose = () => {
    setIsModalVisible(false);
  };

  useOutsideClick(modalRef, handleClose);

  return (
    <div className="fixed top-0 left-0 right-0 z-20">
      <div
        className={cn('', {
          'lg:ml-64': isSidebarVisible,
          'ml-0 lg:ml-20 duration-200': !isSidebarVisible,
        })}
      >
        <Banner
          isLoading={isLoading}
          isSubscription={isSubscription}
          isInTrialPeriod={isInTrialPeriod}
          displayName={displayName}
          tenantId={tenantId}
          subscriptionId={subscriptionId}
          subscriptionEndDateUtc={subscriptionEndDateUtc}
          subscriptionAwaitingCancellation={subscriptionAwaitingCancellation}
          isSubscriptionDeactivated={isSubscriptionDeactivated}
          expiringSubscriptionName={expiringSubscriptionName}
        />

        <div className="flex items-center justify-between bg-white-00 dark:bg-black-900 h-16 duration-200 px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center justify-start">
            <Button
              dataCy="open-sidebar"
              type="button"
              onClick={onClick}
              className="hover:bg-inherit bg-inherit active:bg-inherit dark:hover:bg-inherit dark:bg-inherit p-0"
            >
              <BurgerSvg className="w-5 cursor-pointer focus:outline-none fill-neutral-600 hover:fill-neutral-900 dark:fill-neutral-400 dark:hover:fill-neutral-300" />
            </Button>
            <p className="px-5 text-black-900 dark:text-neutral-100 text-bodyMd">{pageTitle ? t(pageTitle) : ''}</p>
          </div>
          <div data-cy="settings-dropdown" onClick={toggleModal} className="flex items-center cursor-pointer">
            <Avatar
              imagePath={profileAvatar}
              isLoading={isProfilePictureLoading}
              className="mr-2"
              firstName={firstName}
              lastName={lastName}
              email={email}
            />
            <div ref={modalRef}>{isModalVisible && children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
